import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Control,
  Controller,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";
import {
  MiAutocomplete,
  MiBox,
  MiButton,
  MiButtonGroup,
  MiControlledDatePicker,
  MiControlledSelectBox,
  MiControlledTextField,
  MiDetailPageHeader,
  MiIcon,
  MiIconButton,
  MiInputTextbox,
  MiMenu,
  MiModal,
  MiModalConfirm,
  MiPlaceholderList,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  HTTP_STATUSES,
  PERMISSIONS,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { toastService } from "@miplatform/toast";
import {
  ICreatePaymentRequest,
  ICreditMemoDetailsResponse, // IQBOItemMappingListItemResponse,
  IInvoicePayment,
  IInvoiceResponse,
  IPaymentDetailResponse,
  PaymentType,
} from "@miplatform/types";
import { formatDate, getCurrencyString } from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  mdiCancel,
  mdiCurrencyUsd,
  mdiMagnify,
  mdiPlus,
  mdiTrashCanOutline,
} from "@mdi/js";
import { Footer } from "@mibuildweb/components";
import { usePermissions } from "@mibuildweb/hooks";
import {
  Box,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Theme,
  Typography,
} from "@mui/material";
import {
  DataGridPremium,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridCellParams,
  GridColDef,
  GridExpandMoreIcon,
  GridRenderCellParams,
  GridRowId,
  GridRowSelectionModel,
  gridClasses,
  gridDetailPanelExpandedRowIdsSelector,
  gridDetailPanelExpandedRowsContentCacheSelector,
  useGridApiContext,
  useGridApiRef,
  useGridSelector,
} from "@mui/x-data-grid-premium";
import { HomeAndAccountSearch } from "../../../components/HomeAndAccountSearch/HomeAndAccountSearch";
import { InvoiceViewerDrawer } from "./InvoiceViewerDrawer";
import { FIELD_WIDTH } from "./constants";
import { usePostPaymentsViewModel } from "./usePostPaymentsViewModel";

const roundCurrency = (value: number): number => {
  return Math.round(value * 100) / 100;
};

type PaymentFormData = z.infer<typeof paymentFormSchema>;

interface Adjustment {
  note: string;
  amount: number;
}

interface SelectedHome {
  address?: {
    addressLine1?: string;
  };
  id?: string;
  accountType?: string;
  accountName?: string;
  builder?: string;
  community?: string;
}

// Helper to get credit memo IDs that were applied to a specific payment
const getCreditMemoIdsAppliedToPayment = (
  creditMemos: ICreditMemoDetailsResponse[],
  targetPaymentId: string
): string[] => {
  return creditMemos
    .filter((memo) =>
      memo.creditMemoPayments?.some((cp) => cp.paymentId === targetPaymentId)
    )
    .map((memo) => memo.id);
};

// Helper to get the amount this payment applied to a credit memo
const getAppliedAmountForPayment = (
  memo: ICreditMemoDetailsResponse,
  targetPaymentId: string
): number => {
  const usage = memo.creditMemoPayments?.find(
    (cp) => cp.paymentId === targetPaymentId
  );
  return usage?.appliedAmount || 0;
};

const arePaymentErrorsEqual = (
  a: Record<string, { message: string }>,
  b: Record<string, { message: string }>
): boolean => {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) {
    return false;
  }
  return aKeys.every((key) => a[key]?.message === b[key]?.message);
};

const paymentFormSchema = z
  .object({
    customerAccountId: z.string().min(1, "Customer is required"),
    paymentType: z.union([
      z.nativeEnum(PaymentType, {
        required_error: "Payment method is required",
        invalid_type_error: "Payment method is required",
      }),
      z.literal(""),
    ]),
    amount: z
      .number({
        required_error: "Amount is required",
        invalid_type_error: "Amount is required",
      })
      .min(0.0001, "Amount is required"),
    checkNumber: z.string().optional(),
    creditCardReferenceNumber: z.string().optional(),
    depositDate: z
      .string()
      .nullable()
      .refine((val) => !!val, "Deposit date is required"),
    bankAccountQuickBooksId: z.string().min(1, "Bank account is required"),
    // Hidden/computed field to pass applied credit memo total into validation
    appliedCreditMemoTotal: z.number().optional().default(0),
    invoicePayments: z.array(
      z.object({
        invoiceId: z.string(),
        amount: z.number().nonnegative(),
        paymentId: z.string().optional(),
        adjustments: z.array(
          z.object({
            note: z.string(),
            amount: z.number(),
          })
        ),
      })
    ),
  })
  .superRefine((data, ctx) => {
    if (data.paymentType === PaymentType.Check && !data.checkNumber) {
      ctx.addIssue({
        path: ["checkNumber"],
        code: z.ZodIssueCode.custom,
        message: "Check number is required for Check payments",
      });
    }
    if (
      data.paymentType === PaymentType.CreditCard &&
      !data.creditCardReferenceNumber
    ) {
      ctx.addIssue({
        path: ["creditCardReferenceNumber"],
        code: z.ZodIssueCode.custom,
        message: "Reference number is required for Credit Card payments",
      });
    }

    const payments = data.invoicePayments || [];
    // const positive = payments.filter((p) => (p.amount || 0) > 0);

    // if (positive.length === 0) {
    //   ctx.addIssue({
    //     path: ["invoicePayments"],
    //     code: z.ZodIssueCode.custom,
    //     message: "Select at least one invoice with amount > 0",
    //   });
    // }

    const totalAllocated = roundCurrency(
      payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0)
    );
    const appliedCreditMemoTotal = roundCurrency(
      Number(data.appliedCreditMemoTotal || 0)
    );
    const totalAvailable = roundCurrency(data.amount + appliedCreditMemoTotal);
    if (totalAllocated > totalAvailable) {
      ctx.addIssue({
        path: ["invoicePayments"],
        code: z.ZodIssueCode.custom,
        message: "Allocated total exceeds Amount Received + applied credits",
      });
    }
  });

function CustomNoRowsOverlay(): React.ReactNode {
  return (
    <MiPlaceholderList
      icon={mdiCurrencyUsd}
      iconColor="grey"
      text="No customer selected"
      helperText="You must select a customer before their open invoices will appear"
    />
  );
}

function CustomDetailPanelToggle(
  props: Pick<GridRenderCellParams, "id" | "value">
) {
  const { id } = props;
  const apiRef = useGridApiContext();

  // To avoid calling ´getDetailPanelContent` all the time, the following selector
  // gives an object with the detail panel content for each row id.
  const contentCache = useGridSelector(
    apiRef,
    gridDetailPanelExpandedRowsContentCacheSelector
  );

  const expandedRowIds = useGridSelector(
    apiRef,
    gridDetailPanelExpandedRowIdsSelector
  );

  const isExpanded = expandedRowIds.includes(id);

  // If the value is not a valid React element, it means that the row has no detail panel.
  const hasDetail = React.isValidElement(contentCache[id]);

  return (
    <IconButton
      size="small"
      tabIndex={-1}
      disabled={!hasDetail}
      aria-label={isExpanded ? "Close" : "Open"}
      onClick={() => apiRef.current.toggleDetailPanel(id)}
    >
      <GridExpandMoreIcon
        sx={(theme) => ({
          transform: `rotateZ(${isExpanded ? 180 : 0}deg)`,
          transition: theme.transitions.create("transform", {
            duration: theme.transitions.duration.shortest,
          }),
        })}
        fontSize="inherit"
      />
    </IconButton>
  );
}

function PaymentCell({
  fieldIndex,
  control,
  rowId,
  onManualAmountChange,
  canUpdatePayments,
  error,
}: {
  fieldIndex: number;
  control: Control<PaymentFormData>;
  rowId: GridRowId;
  onManualAmountChange: (rowId: GridRowId, val: number) => void;
  canUpdatePayments: boolean;
  error: boolean;
}): React.ReactNode {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
      }}
    >
      <Controller
        name={`invoicePayments.${fieldIndex}.amount`}
        control={control}
        render={({ field: { ref, onChange, ...field }, fieldState }) => (
          <NumericFormat
            {...field}
            value={field.value || ""}
            customInput={TextField}
            thousandSeparator
            required
            placeholder="$0.00"
            decimalScale={2}
            onValueChange={(values) => {
              const val = values.floatValue ?? 0;
              onChange(val);
              onManualAmountChange(rowId, val);
            }}
            fixedDecimalScale
            allowNegative
            allowLeadingZeros={false}
            prefix="$"
            disabled={!canUpdatePayments}
            name={field.name}
            error={error || !!fieldState?.error}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: "32px",
                fontSize: "0.875rem",
              },
            }}
          />
        )}
      />
    </Box>
  );
}

function CreateCreditMemoModal({
  open,
  accountId,
  onClose,
  onSubmit,
}: {
  open: boolean;
  accountId: string;
  onClose: () => void;
  onSubmit: (data: {
    homeId: string;
    accountId: string;
    poNumber: string;
    amount: number;
    notes: string;
  }) => void;
}): React.ReactNode {
  const [selectedHome, setSelectedHome] = useState<SelectedHome | null>(null);

  const creditMemoFormSchema = z.object({
    homeId: z.string().min(1, "Home is required"),
    poNumber: z.string().min(1, "PO Number is required"),
    amount: z
      .number({
        required_error: "Amount is required",
        invalid_type_error: "Amount is required",
      })
      .min(0.01, "Amount must be greater than 0"),
    notes: z.string().optional(),
    accountId: z.string(),
  });

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { isValid },
  } = useForm({
    mode: "onChange",
    resolver: zodResolver(creditMemoFormSchema),
    defaultValues: {
      homeId: "",
      poNumber: "",
      amount: 0,
      notes: "",
      accountId,
    },
  });

  const handleFormSubmit = (data: {
    homeId: string;
    poNumber: string;
    amount: number;
    notes: string;
    accountId: string;
  }) => {
    onSubmit({
      homeId: selectedHome?.id || "",
      poNumber: data.poNumber,
      amount: Number(data.amount),
      notes: data.notes,
      accountId,
    });
    reset();
    onClose();
  };

  const handleCancel = () => {
    reset();
    setSelectedHome(null);
    onClose();
  };

  const handleHomeSelect = (home: SelectedHome) => {
    setSelectedHome(home);
    setValue("homeId", home?.id || "", {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleHomeReset = () => {
    setSelectedHome(null);
    setValue("homeId", "");
  };

  return (
    <MiModal
      open={open}
      closeModal={() => {
        reset();
        onClose();
      }}
      title="Create Backcharge "
      titleIcon={mdiCurrencyUsd}
      width={500}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          color: BUTTON_COLORS.BLACK,
          onClick: handleCancel,
        },
        {
          name: "Create Backcharge",
          disabled: !isValid,
          variant: BUTTON_VARIANTS.CONTAINED,
          onClick: handleSubmit(handleFormSubmit),
        },
      ]}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Find the address for the backcharge you are creating and enter an
          amount
        </Typography>

        <HomeAndAccountSearch
          type="home"
          required
          onSelect={handleHomeSelect}
          onReset={handleHomeReset}
          homeFilters={{ accountId: { type: "guid", value: accountId } }}
          showLogo={false}
          hideCreateNewOption
        />
        {/* <MiControlledSelectBox
          name="qBOItemMappingId"
          formControl={control}
          handleChange={(e: SelectChangeEvent<string>) => {
            setValue("qBOItemMappingId", e.target.value, {
              shouldDirty: true,
            });
          }}
          disableNone
          label="QuickBooks Product"
          options={qBOItemMappingsOptions}
          required
        /> */}

        <MiControlledTextField
          name="poNumber"
          formControl={control}
          label="PO Number"
          required
        />

        <Controller
          name="amount"
          control={control}
          render={({
            field: { ref, value, onChange, ...field },
            fieldState,
          }) => (
            <NumericFormat
              {...field}
              customInput={MiInputTextbox}
              thousandSeparator
              required
              label="Amount"
              decimalScale={2}
              fixedDecimalScale
              allowNegative={false}
              allowLeadingZeros={false}
              prefix="$"
              name={field.name}
              onValueChange={(values) => {
                onChange(values.floatValue ?? 0);
              }}
              textfieldProps={{
                sx: {
                  size: "small",
                  fontSize: "0.875rem",
                },
              }}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />

        <MiControlledTextField
          name="notes"
          formControl={control}
          label="Notes"
          multiline
          rows={3}
        />
      </Box>
    </MiModal>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function CreateRetainageModal({
  open,
  invoice,
  onClose,
  onSubmit,
  defaults,
}: {
  open: boolean;
  invoice: IInvoiceResponse | null;
  onClose: () => void;
  onSubmit: (data: {
    amount: number;
    isPercentage: boolean;
    retainageValue: number;
  }) => void;
  defaults?: { retainagePercent?: number };
}): React.ReactNode {
  const [mode, setMode] = useState<"dollar" | "percent">("percent");
  const [amount, setAmount] = useState<number>(0);

  useEffect(() => {
    if (open) {
      setMode("percent");
      setAmount(defaults?.retainagePercent ?? 0);
    }
  }, [open, invoice, defaults?.retainagePercent]);

  const invoiceAmount = invoice?.totalAmount || 0;

  const retainageValue = useMemo(() => {
    if (mode === "dollar") return amount;
    return (invoiceAmount * amount) / 100;
  }, [mode, amount, invoiceAmount]);

  const handleSubmit = () => {
    onSubmit({
      amount,
      isPercentage: mode === "percent",
      retainageValue,
    });
    onClose();
  };

  return (
    <MiModal
      open={open}
      closeModal={onClose}
      title="Create Retainage Adjustment"
      titleIcon={mdiCurrencyUsd}
      width={500}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          color: BUTTON_COLORS.BLACK,
          onClick: onClose,
        },
        {
          name: "Create Retainage",
          variant: BUTTON_VARIANTS.CONTAINED,
          onClick: handleSubmit,
          disabled: retainageValue <= 0,
        },
      ]}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Create a retainage adjustment for{" "}
          <strong>{invoice?.home?.address?.addressLine1}</strong> on{" "}
          <strong>{invoice?.invoiceNumber}</strong>.
        </Typography>

        <Box display="flex" gap={2} alignItems="flex-start">
          <MiButtonGroup
            data={[
              { value: "dollar", label: "$", sx: { width: 40 } },
              { value: "percent", label: "%", sx: { width: 40 } },
            ]}
            selected={mode}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            handleClick={(item: any) => setMode(item.value)}
            containerStyle={{ height: 40 }}
          />
          <NumericFormat
            value={amount}
            customInput={TextField}
            label="Retainage Amount"
            thousandSeparator
            decimalScale={2}
            onValueChange={(values) => {
              setAmount(values.floatValue ?? 0);
            }}
            fixedDecimalScale={mode === "dollar"}
            allowNegative={false}
            allowLeadingZeros={false}
            prefix={mode === "dollar" ? "$" : ""}
            suffix={mode === "percent" ? "%" : ""}
            fullWidth
            variant="outlined"
            size="small"
          />
        </Box>
        <Box>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="subtitle2" fontWeight="bold">
              {invoice?.invoiceNumber}
            </Typography>
            <Typography variant="subtitle2">Net Change</Typography>
          </Box>
          <Box
            display="flex"
            gap={4}
            justifyContent="space-between"
            alignItems="center"
          >
            <Box flex={1}>
              <Box border="1px solid #e0e0e0" borderRadius={1} p={1} mt={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Invoice Amount
                </Typography>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography>{getCurrencyString(invoiceAmount)}</Typography>
                </Box>
              </Box>
            </Box>
            <Box minWidth={100}>
              <Typography color="error.main" fontWeight="bold" textAlign="end">
                ({getCurrencyString(retainageValue)})
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box>
          <Typography variant="subtitle2" fontWeight="bold">
            {invoice?.invoiceNumber}-R (Retainage Invoice)
          </Typography>
          <Box
            display="flex"
            gap={4}
            justifyContent="space-between"
            alignItems="center"
          >
            <Box flex={1}>
              <Box border="1px solid #e0e0e0" borderRadius={1} p={1} mt={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Invoice Amount
                </Typography>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography>{getCurrencyString(retainageValue)}</Typography>
                </Box>
              </Box>
            </Box>
            <Box minWidth={100}>
              <Typography
                color="success.main"
                fontWeight="bold"
                textAlign="end"
              >
                <span style={{ visibility: "hidden" }}>(</span>
                {getCurrencyString(retainageValue)}
                <span style={{ visibility: "hidden" }}>)</span>
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </MiModal>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function CustomFooter(props: any) {
  const { count, selectedCount, label = "Invoices" } = props;
  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        gap: 4,
        borderTop: "1px solid #e0e0e0",
        backgroundColor: "#fafafa",
      }}
    >
      <Typography variant="body2">
        Total {label}: <strong>{count}</strong>
      </Typography>
      <Typography variant="body2">
        Selected: <strong>{selectedCount}</strong>
      </Typography>
    </Box>
  );
}

function AdjustmentRow({
  adjustment,
  index,
  row,
  onUpdate,
  onDelete,
}: {
  adjustment: Adjustment;
  index: number;
  row: IInvoiceResponse;
  onUpdate: (
    invoiceId: string | number,
    adjustmentIndex: number,
    field: keyof Adjustment,
    value: string | number
  ) => void;
  onDelete: (invoiceId: string | number, adjustmentIndex: number) => void;
}) {
  const [mode, setMode] = useState<"dollar" | "percent">("percent");
  const baseAmount = row.totalAmount || 0;

  const [localValue, setLocalValue] = useState<string>("");
  const [isFocused, setIsFocused] = useState(false);
  const lastCommittedRef = React.useRef(adjustment.amount);

  const computedDisplayValue = useMemo(() => {
    if (mode === "dollar") return adjustment.amount;
    if (!baseAmount) return 0;
    return (adjustment.amount / baseAmount) * 100;
  }, [mode, adjustment.amount, baseAmount]);

  useEffect(() => {
    if (!isFocused) {
      const precision = mode === "percent" ? 4 : 2;
      setLocalValue(computedDisplayValue.toFixed(precision));
    }
  }, [computedDisplayValue, isFocused, mode]);

  useEffect(() => {
    lastCommittedRef.current = adjustment.amount;
  }, [adjustment.amount]);

  const handleBlur = () => {
    setIsFocused(false);
    const numericValue = parseFloat(localValue) || 0;

    let newAmount = numericValue;
    if (mode === "percent") {
      newAmount = (baseAmount * numericValue) / 100;
    }

    newAmount = Math.round(newAmount * 100) / 100;

    if (Math.abs(newAmount - lastCommittedRef.current) > 0.001) {
      lastCommittedRef.current = newAmount;
      onUpdate(row.id, index, "amount", newAmount);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleValueChange = (values: {
    value: string;
    floatValue?: number;
  }) => {
    if (isFocused) {
      setLocalValue(values.value);
    }
  };

  const handleModeChange = (newMode: "dollar" | "percent") => {
    if (isFocused) {
      handleBlur();
    }
    setMode(newMode);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 4,
        py: 1,
        px: 2,
        borderBottom: "1px solid #e0e0e0",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          color: "text.primary",
        }}
      >
        Discount Adjustment {index + 1}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 4, flex: 1 }}>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <MiInputTextbox
            label="Note"
            value={adjustment.note}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onUpdate(row.id, index, "note", e.target.value)
            }
            sx={{
              width: "380px",
              "& .MuiOutlinedInput-root": {
                height: "34px",
                fontSize: "0.875rem",
              },
              "& .MiInputLabel-root": {
                fontSize: "0.875rem",
              },
            }}
          />
        </Box>

        <Box display="flex" gap={2} alignItems="flex-start">
          <MiButtonGroup
            data={[
              { value: "dollar", label: "$", sx: { width: 40 } },
              { value: "percent", label: "%", sx: { width: 40 } },
            ]}
            selected={mode}
            handleClick={(item: { value: "dollar" | "percent" }) =>
              handleModeChange(item.value)
            }
            containerStyle={{ height: 34 }}
          />
          <Box sx={{ minWidth: 120 }}>
            <NumericFormat
              value={isFocused ? localValue : computedDisplayValue}
              label="Amount"
              customInput={TextField}
              thousandSeparator
              decimalScale={mode === "percent" ? 4 : 2}
              fixedDecimalScale={!isFocused}
              onValueChange={handleValueChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              allowNegative
              allowLeadingZeros={false}
              prefix={mode === "dollar" ? "$" : ""}
              suffix={mode === "percent" ? "%" : ""}
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "34px",
                  fontSize: "0.875rem",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.875rem",
                },
              }}
            />
          </Box>
        </Box>
        <Box>
          <Typography variant="body2" fontWeight="400" color="text.secondary">
            Discount
          </Typography>
          <Typography
            variant="body2"
            fontWeight="bold"
            // color={adjustment.amount < 0 ? "error.main" : "success.main"}
            sx={{ minWidth: 100 }}
          >
            {adjustment.amount < 0 ? "-" : ""}
            {getCurrencyString(Math.abs(adjustment.amount))}
          </Typography>
        </Box>

        <MiIconButton
          onClick={() => onDelete(row.id, index)}
          className="adjustment-delete-btn"
        >
          <MiIcon color="grey" path={mdiTrashCanOutline} />
        </MiIconButton>
      </Box>
    </Box>
  );
}

export const PostPayments: React.FC = () => {
  const { classes, theme } = useStyles();
  const { navigate, params } = useRouter();

  const { id: paymentId } = params;

  const apiRef = useGridApiRef();
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(
    []
  );
  const [creditMemoSelectionModel, setCreditMemoSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [initialCreditMemoSelection, setInitialCreditMemoSelection] =
    useState<GridRowSelectionModel>([]);

  const [expandedRows, setExpandedRows] = useState<Set<string | number>>(
    new Set()
  );

  const [paymentData, setPaymentData] = useState<IPaymentDetailResponse | null>(
    null
  );
  const [isInitializing, setIsInitializing] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<
    IInvoiceResponse | ICreditMemoDetailsResponse | null
  >(null);

  const [paymentErrors, setPaymentErrors] = useState<
    Record<string, { message: string }>
  >({});

  const [creditMemoModalOpen, setCreditMemoModalOpen] = useState(false);
  const [creditMemoToVoid, setCreditMemoToVoid] = useState<{
    id: string;
    modifiedOn: string;
  } | null>(null);

  const [retainageModalOpen, setRetainageModalOpen] = useState(false);
  const [retainageInvoice, setRetainageInvoice] =
    useState<IInvoiceResponse | null>(null);
  const { hasPermission } = usePermissions();

  const {
    search,
    setSearch,
    isLoading,
    getCustomers,
    customers,
    setInvoices,
    getInvoices,
    invoices,
    postPayment,
    updatePayment,
    bankAccountsOptions,
    debouncedSearch,
    getBankAccounts,
    getPaymentDetails,
    getCreditMemos,
    setCreditMemos,
    creditMemos,
    createCreditMemo,
    voidCreditMemo,
    createRetainage,
  } = usePostPaymentsViewModel({ paymentId: paymentId ?? "" });

  const defaultFormValues: Partial<PaymentFormData> = {
    customerAccountId: "",
    amount: 0,
    depositDate: null,
    bankAccountQuickBooksId: "",
    paymentType: "",
    appliedCreditMemoTotal: 0,
    invoicePayments: [],
  };

  useEffect(() => {
    if (paymentId) {
      setIsInitializing(true);
      (async () => {
        const data = await getPaymentDetails(paymentId);

        if (data) {
          setPaymentData(data);
          reset(data);

          const selectedIds =
            (data.invoicePayments as IInvoicePayment[]).map(
              (p: IInvoicePayment) => p.invoiceId
            ) ?? [];
          setSelectionModel(selectedIds);
        }
      })();
    }
  }, []);

  useEffect(() => {
    if (paymentId && creditMemos.length > 0) {
      const appliedIds = getCreditMemoIdsAppliedToPayment(
        creditMemos,
        paymentId
      );
      setCreditMemoSelectionModel(appliedIds);
      setInitialCreditMemoSelection(appliedIds);
    }
  }, [paymentId, creditMemos]);

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    setError,
    formState: { errors, isValid, isDirty },
    reset,
    watch,
  } = useForm<PaymentFormData>({
    resolver: zodResolver(paymentFormSchema),
    defaultValues: defaultFormValues,
    mode: "onChange",
  });

  const { replace } = useFieldArray({
    control,
    name: "invoicePayments",
  });
  const customerAccountId = watch("customerAccountId");
  const invoicePaymentsWatch = useWatch({ control, name: "invoicePayments" });
  const amountReceived = watch("amount") || 0;
  const paymentType = watch("paymentType");
  const [customerInputValue, setCustomerInputValue] = useState("");

  const accountDefaults = useMemo(() => {
    if (customerAccountId) {
      const selectedCustomer = customers.find(
        (c) => c.id === customerAccountId
      );
      if (selectedCustomer) {
        return {
          defaultRetainagePercentage:
            selectedCustomer.defaultRetainagePercentage || 0,
          defaultDiscountPercentage:
            selectedCustomer.defaultDiscountPercentage || 0,
        };
      }
    }
    return { defaultRetainagePercentage: 0, defaultDiscountPercentage: 0 };
  }, [customerAccountId, customers]);

  const getAmountDue = useCallback(
    (index: number) => {
      if (paymentId) {
        const remainingDue = invoices[index]?.invoicePayments?.reduce(
          (sum, p) =>
            p.paymentId === paymentId
              ? sum + (getValues(`invoicePayments.${index}.amount`) || 0)
              : p.amount,
          0
        );
        const totalAmount = Number(invoices[index]?.totalAmount) || 0;
        const remaining = Number(remainingDue) || 0;

        return roundCurrency(totalAmount - remaining);
      }
      if (getValues(`invoicePayments.${index}.amount`)) {
        return roundCurrency(getValues(`invoicePayments.${index}.amount`));
      }
      return roundCurrency(invoices[index]?.amountDue || 0);
    },
    [paymentId, invoices, getValues]
  );

  const totals = useMemo(() => {
    const totalPayments = roundCurrency(
      invoicePaymentsWatch?.reduce(
        (sum, payment) => sum + (Number(payment.amount) || 0),
        0
      ) || 0
    );

    const adjustmentsList = (invoicePaymentsWatch ?? []).flatMap(
      (p) => p.adjustments ?? []
    );
    const totalAdjustments = roundCurrency(
      adjustmentsList.reduce((sum, adj) => sum + (Number(adj.amount) || 0), 0)
    );

    // prorated discount
    const discountTotal = roundCurrency(
      invoices.reduce((sum, inv, index) => {
        if (!selectionModel.includes(inv.id)) return sum;
        const payment =
          Math.min(
            inv.totalAmount,
            Number(invoicePaymentsWatch?.[index]?.amount)
          ) || 0;

        const total = Number(inv.totalAmount) || 0;

        const existingDiscounts = roundCurrency(
          (invoicePaymentsWatch?.[index]?.adjustments ?? []).reduce(
            (sum, adj) => sum + (Number(adj.amount) || 0),
            0
          )
        );

        const denominator = total - existingDiscounts;

        if (denominator > 0) {
          return sum + (payment / denominator) * existingDiscounts;
        }
        return sum;
      }, 0)
    );

    // prorated retainage
    const retainageTotal = roundCurrency(
      invoices.reduce((sum, inv, index) => {
        if (!selectionModel.includes(inv.id)) return sum;

        // Only calculate retainage for invoices that have a retainage child
        if (!inv.retainageChildId) return sum;

        const matchingPayment = invoicePaymentsWatch?.find(
          (payment) => payment.invoiceId === inv.id
        );

        const payment =
          Math.min(inv.totalAmount, Number(matchingPayment?.amount)) || 0;
        const total = Number(inv.totalAmount) || 0;
        const totalRetainage = Number(inv.totalRetainage) || 0;

        if (totalRetainage === 0) return sum;

        // Calculate existing discounts to get the denominator
        const existingDiscounts = paymentId
          ? Number(inv.totalDiscounts) || 0
          : roundCurrency(
              (invoicePaymentsWatch?.[index]?.adjustments ?? [])
                .filter((adj) => true) // All adjustments are now discounts
                .reduce((sum, adj) => sum + (Number(adj.amount) || 0), 0)
            );

        const denominator = total - existingDiscounts;

        if (denominator > 0) {
          return sum + (payment / denominator) * totalRetainage;
        }
        return sum;
      }, 0)
    );

    const selectedInvoices =
      invoicePaymentsWatch?.filter(
        (payment) => (Number(payment.amount) || 0) > 0
      ).length || 0;

    const totalAmountDue = roundCurrency(
      invoices.reduce((sum, _invoice, index) => {
        if (!selectionModel.includes(invoices[index]?.id)) return sum;
        return sum + (getAmountDue(index) || 0);
      }, 0)
    );

    const selectedCreditMemosTotal = roundCurrency(
      creditMemos
        .filter((memo) => creditMemoSelectionModel.includes(memo.id))
        .reduce((sum, memo) => {
          const baseOpenAmount = Number(memo.openAmount) || 0;
          // In edit mode, add back what this payment applied
          if (paymentId) {
            const appliedAmount = getAppliedAmountForPayment(memo, paymentId);
            return sum + baseOpenAmount + appliedAmount;
          }
          return sum + baseOpenAmount;
        }, 0)
    );
    const backcharges = roundCurrency(
      Math.min(
        selectedCreditMemosTotal,
        Math.max(0, totalPayments - amountReceived)
      )
    );
    return {
      totalPayments,
      totalAdjustments,
      discountTotal,
      retainageTotal,
      selectedInvoices,
      totalAmountDue,
      backcharges,
      selectedCreditMemosTotal,
    };
  }, [
    invoicePaymentsWatch,
    invoices,
    selectionModel,
    creditMemos,
    amountReceived,
    creditMemoSelectionModel,
  ]);

  useEffect(() => {
    setValue("appliedCreditMemoTotal", totals.selectedCreditMemosTotal ?? 0, {
      shouldDirty: false,
      shouldValidate: true,
    });
  }, [totals.selectedCreditMemosTotal, setValue]);

  const invoiceIndexMap = useMemo(() => {
    const map = new Map<string | number, number>();
    invoices.forEach((invoice, index) => {
      map.set(invoice.id, index);
    });
    return map;
  }, [invoices]);

  const canUpdatePayments =
    hasPermission(PERMISSIONS.PAYMENT.UPDATE) && amountReceived > 0;
  const canCreateCreditMemos = hasPermission(PERMISSIONS.CREDIT_MEMO.CREATE);
  const canVoidCreditMemos = hasPermission(PERMISSIONS.CREDIT_MEMO.VOID);

  const calculateInvoiceDiscounts = useCallback(
    (invoiceIndex: number): number => {
      const adjustments =
        invoicePaymentsWatch?.[invoiceIndex]?.adjustments ?? [];
      return roundCurrency(
        adjustments.reduce((sum, adj) => sum + (Number(adj.amount) || 0), 0)
      );
    },
    [invoicePaymentsWatch]
  );

  // Gross "Amt Due" before any discounts. backend amountDue already has the
  // saved discounts subtracted, so we add them back, plus whatever this payment
  // already applied (edit mode). This is exactly what the Amt Due column shows,
  // so both read from here and can't drift apart.
  const getGrossAmountDue = useCallback(
    (invoice: IInvoiceResponse | undefined): number => {
      const base = Number(invoice?.amountDue) || 0;
      const priorDiscounts = Number(invoice?.totalDiscounts) || 0;
      // in edit mode the gross also includes what this payment already paid
      const thisPaymentAmount = paymentId
        ? Number(
            paymentData?.invoicePayments?.find(
              (p) => String(p.invoiceId) === String(invoice?.id)
            )?.amount
          ) || 0
        : 0;

      return base + priorDiscounts + thisPaymentAmount;
    },
    [paymentId, paymentData]
  );

  // How much should actually land in the Payment box when you tick an invoice:
  // the gross due minus whatever discounts are sitting in the form right now
  // (saved ones + anything just typed). Without this the checkbox was filling
  // the full pre-discount amount and the balance went negative.
  const getEffectiveAmountDue = useCallback(
    (invoiceIndex: number): number => {
      const originalDue = getGrossAmountDue(invoices[invoiceIndex]);
      const currentDiscounts = calculateInvoiceDiscounts(invoiceIndex);

      return roundCurrency(Math.max(0, originalDue - currentDiscounts));
    },
    [invoices, getGrossAmountDue, calculateInvoiceDiscounts]
  );

  // Helper function to get the original amount due for an invoice
  const getOriginalAmountDue = useCallback(
    (invoice: IInvoiceResponse, invoiceIndex: number): number => {
      if (!paymentId) {
        // CREATE mode: amountDue from backend has discounts already applied
        // Add back existing discounts to show the original amount due
        const baseAmountDue = Number(invoice.amountDue) || 0;
        const existingDiscounts = Number(invoice.totalDiscounts) || 0;
        return roundCurrency(baseAmountDue + existingDiscounts);
      }

      // EDIT mode: Balance = (amountDue + thisPaymentAmount + thisPaymentDiscount) - payment - editedDiscount
      const thisPayment = invoice.invoicePayments?.find(
        (p) => String(p.paymentId) === String(paymentId)
      );
      const thisPaymentAmount = thisPayment?.amount || 0;
      const thisPaymentDiscount =
        thisPayment?.adjustments?.reduce(
          (sum, adj) => sum + (Number(adj.amount) || 0),
          0
        ) || 0;

      return roundCurrency(
        Number(invoice.amountDue) +
          Number(thisPaymentAmount) +
          thisPaymentDiscount
      );
    },
    [paymentId]
  );

  const getCreditMemoDisplayOpenAmount = useCallback(
    (memo: ICreditMemoDetailsResponse): number => {
      const baseOpenAmount = Number(memo.openAmount) || 0;

      // CREATE mode
      if (!paymentId) {
        return baseOpenAmount;
      }

      // EDIT mode
      const appliedAmount = getAppliedAmountForPayment(memo, paymentId);

      if (appliedAmount > 0) {
        // This payment already used this memo
        // Show full available: openAmount + what this payment applied
        return roundCurrency(baseOpenAmount + appliedAmount);
      }

      return baseOpenAmount;
    },
    [paymentId]
  );

  const getInvoicePaymentError = useCallback(
    (invoiceIndex: number, overrideAmount?: number): string | null => {
      const invoice = invoices[invoiceIndex];
      if (!invoice) {
        return null;
      }

      const currentAmount = roundCurrency(
        typeof overrideAmount === "number"
          ? overrideAmount
          : Number(invoicePaymentsWatch?.[invoiceIndex]?.amount) || 0
      );

      if (currentAmount <= 0) {
        return null;
      }

      const discountTotal = calculateInvoiceDiscounts(invoiceIndex);
      const originalAmountDue = getOriginalAmountDue(invoice, invoiceIndex);
      const totalApplied = roundCurrency(currentAmount + discountTotal);

      if (totalApplied > originalAmountDue) {
        return `Payment + Discounts (${getCurrencyString(
          totalApplied
        )}) cannot exceed Amount Due (${getCurrencyString(originalAmountDue)})`;
      }

      const othersTotal = roundCurrency(
        (invoicePaymentsWatch ?? []).reduce((sum, payment, idx) => {
          if (idx === invoiceIndex) {
            return sum;
          }
          return sum + (Number(payment?.amount) || 0);
        }, 0)
      );

      const remainingAllowed = roundCurrency(
        Math.max(
          amountReceived + totals.selectedCreditMemosTotal - othersTotal,
          0
        )
      );

      if (currentAmount > remainingAllowed) {
        return `Payment exceeds available funds (${getCurrencyString(
          remainingAllowed
        )})`;
      }

      return null;
    },
    [
      invoices,
      invoicePaymentsWatch,
      calculateInvoiceDiscounts,
      getOriginalAmountDue,
      amountReceived,
      totals.selectedCreditMemosTotal,
    ]
  );

  const resetToDefaults = useCallback(() => {
    reset(defaultFormValues);
    setCustomerInputValue("");
    setSelectionModel([]);
    setCreditMemoSelectionModel([]);
    setInitialCreditMemoSelection([]);
    setInvoices([]);
    setSearch("");
    setExpandedRows(new Set());
    setPaymentErrors({});
    apiRef.current.setRowSelectionModel([]);
  }, [defaultFormValues]);

  const refreshEditMode = useCallback(async () => {
    if (!paymentId) return;

    const latest = await getPaymentDetails(paymentId);
    if (latest) {
      setPaymentData(latest);
      reset(latest, { keepDefaultValues: false });
      const selectedIds =
        (latest.invoicePayments as IInvoicePayment[])
          ?.filter((p: IInvoicePayment) => p.amount > 0)
          .map((p: IInvoicePayment) => p.invoiceId) ?? [];
      setSelectionModel(selectedIds);
      if (latest.customerAccountId) {
        await getCreditMemos(latest.customerAccountId);
      }
    }
  }, [paymentId, getPaymentDetails, getCreditMemos]);

  const handleSuccessfulSubmission = useCallback(
    async (shouldStayAndReset: boolean) => {
      if (!shouldStayAndReset) {
        navigate(`/accounting/payments`);
        return;
      }

      if (paymentId) {
        await refreshEditMode();
      } else {
        resetToDefaults();
      }
    },
    [paymentId, refreshEditMode, resetToDefaults]
  );

  const handleCancel = useCallback(() => {
    if (paymentId && paymentData) {
      // EDIT MODE: Reset to original payment data
      // Exclude invoicePayments from reset to preserve all customer invoices
      const { invoicePayments, ...paymentDataWithoutInvoices } = paymentData;
      reset(paymentDataWithoutInvoices, { keepDefaultValues: false });

      setSearch("");
      setExpandedRows(new Set());
      setPaymentErrors({});

      const selectedIds =
        (paymentData.invoicePayments as IInvoicePayment[]).map(
          (p: IInvoicePayment) => p.invoiceId
        ) ?? [];
      setSelectionModel(selectedIds);
      // resetting back to original applied credit memos
      const appliedMemoIds = getCreditMemoIdsAppliedToPayment(
        creditMemos,
        paymentId
      );
      setCreditMemoSelectionModel(appliedMemoIds);
    } else {
      // CREATE MODE: Reset to defaults and clear everything
      resetToDefaults();
    }
  }, [paymentId, paymentData, creditMemos, resetToDefaults]);

  const handleManualAmountChange = (rowId: GridRowId, raw: number) => {
    const invoiceIndex = invoiceIndexMap.get(rowId) ?? -1;

    if (invoiceIndex < 0) return;

    // Don't auto-cap the value - let user type what they want
    const val = raw;
    const errorKey = `invoice-${rowId}`;

    const errorMessage = getInvoicePaymentError(invoiceIndex, val);
    setPaymentErrors((prev) => {
      const next = { ...prev };
      if (errorMessage) {
        next[errorKey] = { message: errorMessage };
      } else {
        delete next[errorKey];
      }
      return next;
    });

    setSelectionModel((prev) => {
      const has = prev.includes(rowId);
      if (val > 0 && !has) return [...prev, rowId];
      if (val === 0 && has) return prev.filter((id) => id !== rowId);
      return prev;
    });
  };

  useEffect(() => {
    setPaymentErrors((prev) => {
      const next: Record<string, { message: string }> = {};
      invoices.forEach((invoice, index) => {
        const message = getInvoicePaymentError(index);
        if (message) {
          next[`invoice-${invoice.id}`] = { message };
        }
      });

      if (arePaymentErrorsEqual(prev, next)) {
        return prev;
      }
      return next;
    });
  }, [invoices, getInvoicePaymentError]);

  const handleAdjustmentClick = (invoiceId: number) => {
    addNewAdjustment(invoiceId);
    const newExpandedRows = new Set(expandedRows);
    newExpandedRows.add(invoiceId);
    setExpandedRows(newExpandedRows);
  };

  const handleRetainageClick = (invoice: IInvoiceResponse) => {
    setRetainageInvoice(invoice);
    setRetainageModalOpen(true);
  };

  const handleRetainageSubmit = (data: {
    amount: number;
    isPercentage: boolean;
    retainageValue: number;
  }) => {
    if (retainageInvoice) {
      createRetainage({
        invoiceId: retainageInvoice.id,
        data: {
          retainageAmount: data.retainageValue,
          modifiedOn: retainageInvoice.modifiedOn,
        },
      });
    }
  };

  const addNewAdjustment = (invoiceId: string | number) => {
    const invoiceIndex = invoiceIndexMap.get(invoiceId) ?? -1;
    if (invoiceIndex !== -1) {
      const inv = invoices[invoiceIndex];
      const discountPct = accountDefaults.defaultDiscountPercentage || 0;
      const defaultAmount = ((inv?.totalAmount || 0) * discountPct) / 100;
      const newAdjustment: Adjustment = {
        note: "Discount",
        amount: defaultAmount,
      };
      const current =
        getValues(`invoicePayments.${invoiceIndex}.adjustments`) ?? [];
      setValue(
        `invoicePayments.${invoiceIndex}.adjustments`,
        [...current, newAdjustment],
        { shouldDirty: true, shouldValidate: true }
      );

      setSelectionModel((prev) => {
        if (!prev.includes(invoiceId)) {
          return [...prev, invoiceId];
        }
        return prev;
      });
    }
  };

  const updateAdjustment = (
    invoiceId: string | number,
    adjustmentIndex: number,
    field: keyof Adjustment,
    value: string | number
  ) => {
    const invoiceIndex = invoiceIndexMap.get(invoiceId) ?? -1;
    if (invoiceIndex === -1) return;

    const foundInvoice = invoices[invoiceIndex];
    const current =
      getValues(`invoicePayments.${invoiceIndex}.adjustments`) ?? [];
    const updatedAdjustments = current.map((adj: Adjustment, idx: number) =>
      idx === adjustmentIndex ? { ...adj, [field]: value } : adj
    );

    // If updating the amount field on a Discount, validate it won't cause negative balance
    if (field === "amount") {
      const adjustment = current[adjustmentIndex];
      if (adjustment) {
        const currentPayment = roundCurrency(
          getValues(`invoicePayments.${invoiceIndex}.amount`) || 0
        );
        const originalAmountDue = getOriginalAmountDue(
          foundInvoice,
          invoiceIndex
        );

        // Calculate total discounts with the new value
        const totalDiscounts = roundCurrency(
          updatedAdjustments.reduce(
            (sum, adj) => sum + (Number(adj.amount) || 0),
            0
          )
        );

        const totalApplied = roundCurrency(currentPayment + totalDiscounts);

        const errorKey = `invoice-${invoiceId}`;

        // Validate but don't block the update - just show error
        if (totalApplied > originalAmountDue) {
          setPaymentErrors((prev) => ({
            ...prev,
            [errorKey]: {
              message: `Payment + Discounts (${getCurrencyString(
                totalApplied
              )}) cannot exceed Amount Due (${getCurrencyString(
                originalAmountDue
              )})`,
            },
          }));
        } else {
          setPaymentErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[errorKey];
            return newErrors;
          });
        }
      }
    }

    setValue(
      `invoicePayments.${invoiceIndex}.adjustments`,
      updatedAdjustments,
      { shouldDirty: true }
    );
  };

  const deleteAdjustment = (
    invoiceId: string | number,
    adjustmentIndex: number
  ) => {
    const invoiceIndex = invoiceIndexMap.get(invoiceId) ?? -1;
    if (invoiceIndex === -1) return;

    const current =
      getValues(`invoicePayments.${invoiceIndex}.adjustments`) ?? [];
    const deletedAdjustment = current[adjustmentIndex];
    const updatedAdjustments = current.filter(
      (_adj: Adjustment, idx: number) => idx !== adjustmentIndex
    );

    setValue(
      `invoicePayments.${invoiceIndex}.adjustments`,
      updatedAdjustments,
      { shouldDirty: true, shouldValidate: true }
    );

    // Re-validate after deletion to clear any errors if applicable
    if (deletedAdjustment) {
      const foundInvoice = invoices[invoiceIndex];
      const currentPayment = roundCurrency(
        getValues(`invoicePayments.${invoiceIndex}.amount`) || 0
      );
      const originalAmountDue = getOriginalAmountDue(
        foundInvoice,
        invoiceIndex
      );

      const totalDiscounts = roundCurrency(
        updatedAdjustments.reduce(
          (sum, adj) => sum + (Number(adj.amount) || 0),
          0
        )
      );

      const totalApplied = roundCurrency(currentPayment + totalDiscounts);
      const errorKey = `invoice-${invoiceId}`;

      if (totalApplied <= originalAmountDue) {
        setPaymentErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[errorKey];
          return newErrors;
        });
      }
    }

    const currentPaymentAmount =
      getValues(`invoicePayments.${invoiceIndex}.amount`) || 0;
    if (updatedAdjustments.length === 0 && currentPaymentAmount === 0) {
      setSelectionModel((prev) => prev.filter((id) => id !== invoiceId));
    }
  };

  const getDetailPanelContent = ({ row }: { row: IInvoiceResponse }) => {
    const idx = invoiceIndexMap.get(row.id) ?? -1;
    const invoiceAdjustments = invoicePaymentsWatch?.[idx]?.adjustments ?? [];

    if (!invoiceAdjustments || invoiceAdjustments.length === 0) {
      return null;
    }

    return (
      <Box
        sx={{
          p: 0,
          backgroundColor: "#fafafa",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
        }}
      >
        {invoiceAdjustments.map((adjustment: Adjustment, index: number) => (
          <AdjustmentRow
            key={index}
            adjustment={adjustment}
            index={index}
            row={row}
            onUpdate={updateAdjustment}
            onDelete={deleteAdjustment}
          />
        ))}
      </Box>
    );
  };

  useEffect(() => {
    getCustomers();
    getBankAccounts();
  }, []);

  useEffect(() => {
    if (customerAccountId) {
      getCreditMemos(customerAccountId);
    } else {
      setCreditMemos([]);
    }
  }, [customerAccountId]);

  useEffect(() => {
    if (customerAccountId) {
      getInvoices({ customerAccountId });
      setPaymentErrors({});
      return;
    }
    setInvoices([]);
    setPaymentErrors({});
  }, [customerAccountId, debouncedSearch]);

  useEffect(() => {
    if (invoices.length === 0) return;

    if (!paymentId) {
      // CREATE mode: Use totalDiscounts and discountNote from invoice

      const currentPayments = getValues("invoicePayments") || [];
      const existingPaymentMap = new Map(
        currentPayments.map((p) => [String(p.invoiceId), p])
      );

      const invoicePayments = invoices.map((invoice) => {
        const existingPayment = existingPaymentMap.get(String(invoice.id));

        if (existingPayment) {
          return existingPayment;
        }

        // Otherwise, create a new entry with defaults
        const adjustments: Adjustment[] = [];

        // If invoice has totalDiscounts > 0, add it as adjustment
        if (invoice.totalDiscounts && invoice.totalDiscounts > 0) {
          adjustments.push({
            note: invoice.discountNote || "Discount",
            amount: invoice.totalDiscounts,
          });
        }

        return {
          invoiceId: invoice.id.toString(),
          amount: 0,
          adjustments,
        };
      });

      replace(invoicePayments);

      // Auto-expand rows that have discounts
      const rowsWithDiscounts = invoicePayments
        .filter((payment) => payment.adjustments.length > 0)
        .map((payment) => payment.invoiceId);

      if (rowsWithDiscounts.length > 0) {
        setExpandedRows(new Set(rowsWithDiscounts));
      } else {
        setExpandedRows(new Set());
      }
    } else {
      // EDIT mode: Map invoices to their payment amounts for this payment
      const invoicePayments = invoices.map((invoice) => {
        const thisPayment = paymentData?.invoicePayments?.find(
          (p) => String(p.invoiceId) === String(invoice.id)
        );

        const adjustments: Adjustment[] =
          invoice.totalDiscounts && invoice.totalDiscounts > 0
            ? [
                {
                  note: invoice.discountNote || "Discount",
                  amount: invoice.totalDiscounts,
                },
              ]
            : [];

        return {
          invoiceId: invoice.id,
          amount: Number(thisPayment?.amount) || 0,
          adjustments,
        };
      });

      replace(invoicePayments);

      const selectedIds = invoicePayments
        .filter((payment) => (payment.amount || 0) > 0)
        .map((payment) => payment.invoiceId);
      setSelectionModel(selectedIds);
      if (isInitializing) {
        setTimeout(() => {
          reset(getValues(), { keepDefaultValues: false });
          setIsInitializing(false);
        }, 0);
      }

      // Auto-expand rows that have adjustments in edit mode
      const rowsWithAdjustments = invoicePayments
        .filter((payment) => (payment.adjustments || []).length > 0)
        .map((payment) => payment.invoiceId);

      if (rowsWithAdjustments.length > 0) {
        setExpandedRows(new Set(rowsWithAdjustments));
      } else {
        setExpandedRows(new Set());
      }
    }
  }, [invoices, paymentId, paymentData]);

  const onSubmit = async (
    data: PaymentFormData,
    shouldStayAndReset?: boolean
  ) => {
    const filteredInvoicePayments = data.invoicePayments.filter((payment) => {
      const hasPayment = (payment.amount || 0) > 0;
      const hasAdjustments = (payment.adjustments || []).some(
        (adj) => (adj.amount || 0) !== 0
      );
      return hasPayment || hasAdjustments;
    });

    const invoicePaymentsForSubmit = filteredInvoicePayments.map((payment) => ({
      ...payment,
      discountAmount:
        payment.adjustments.length > 0 ? payment.adjustments[0].amount : 0,
      discountNote:
        payment.adjustments.length > 0 ? payment.adjustments[0].note : "",
      adjustments: payment.adjustments,
    }));

    const payload: ICreatePaymentRequest = {
      ...data,
      appliedCreditMemos: creditMemoSelectionModel.map((id) => id.toString()),
      invoicePayments: invoicePaymentsForSubmit,
      depositDate: data.depositDate ?? "",
    };

    const onSuccess = () =>
      handleSuccessfulSubmission(shouldStayAndReset ?? false);

    if (paymentId) {
      await updatePayment(
        paymentId,
        payload,
        paymentData?.modifiedOn ?? "",
        onSuccess
      );
    } else {
      await postPayment(payload, onSuccess);
    }
  };

  const getInvoiceBalance = (row: IInvoiceResponse): number => {
    const index = invoiceIndexMap.get(row.id) ?? -1;
    if (index < 0) return roundCurrency(row.amountDue || 0);

    const originalAmountDue = getOriginalAmountDue(row, index);
    const currentPaymentAmount = roundCurrency(
      invoicePaymentsWatch?.[index]?.amount || 0
    );
    const discountTotal = calculateInvoiceDiscounts(index);

    // Balance = Original Amount Due - Payment - Discounts
    // Can be negative if payment + discounts exceed amount due
    const balance = roundCurrency(
      originalAmountDue - currentPaymentAmount - discountTotal
    );

    return balance;
  };

  const hasPaymentErrors = Object.keys(paymentErrors).length > 0;

  // Check if credit memo selection has changed from initial state
  const hasCreditMemoChanges = useMemo(() => {
    if (creditMemoSelectionModel.length !== initialCreditMemoSelection.length) {
      return true;
    }
    const sortedCurrent = [...creditMemoSelectionModel].sort((a, b) =>
      String(a).localeCompare(String(b))
    );
    const sortedInitial = [...initialCreditMemoSelection].sort((a, b) =>
      String(a).localeCompare(String(b))
    );
    return sortedCurrent.some((id, index) => id !== sortedInitial[index]);
  }, [creditMemoSelectionModel, initialCreditMemoSelection]);

  const footerActions = [
    {
      name: "Cancel",
      variant: BUTTON_VARIANTS.TEXT,
      color: "black",
      onClick: handleCancel,
    },
    {
      name: paymentId ? "Update & Add Another" : "Post & Add Another",
      variant: BUTTON_VARIANTS.OUTLINED,
      disabled: !isValid || hasPaymentErrors || isLoading,
      onClick: handleSubmit((data) => onSubmit(data, true)),
    },
    {
      name: paymentId ? "Update Payment" : "Post Payment",
      variant: BUTTON_VARIANTS.CONTAINED,
      disabled: !isValid || hasPaymentErrors || isLoading,
      onClick: handleSubmit((data) => onSubmit(data)),
    },
  ];

  const columns: GridColDef<IInvoiceResponse>[] = [
    {
      field: "addressLine1",
      headerName: "Address",
      flex: 2,
      minWidth: 250,
      valueGetter: (value, row) => row.home?.address?.addressLine1 || "",
      renderCell: (params: GridRenderCellParams<IInvoiceResponse, string>) => {
        const address = params.row.home?.address?.addressLine1 || "";
        return (
          <Box display="flex" alignItems="center" height="100%">
            <Typography
              variant="body2"
              sx={{
                cursor: "pointer",
                color: "primary.main",
                textDecoration: "underline",
                "&:hover": {
                  color: "primary.dark",
                },
              }}
              onClick={() => handleDocumentClick(params.row)}
            >
              {address}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "orderNumber",
      headerName: "Order #",
      flex: 1.3,
      minWidth: 120,
    },
    {
      field: "poNumber",
      headerName: "PO #",
      flex: 1.3,
      minWidth: 120,
      valueGetter: (value, row) =>
        row.invoiceBillingCommitments
          .map((inv) => inv.billingCommitmentNumber)
          .join(", ") || "",
    },
    {
      field: "invoiceNumber",
      headerName: "Invoice #",
      flex: 1.3,
      minWidth: 130,
    },
    {
      field: "invoicedDate",
      headerName: "Invoice Date",
      flex: 1,
      minWidth: 110,
      valueFormatter: (value, row) => formatDate(value, "MM/DD/YYYY"),
    },
    {
      field: "totalAmount",
      headerName: "Original",
      flex: 1,
      headerAlign: "center",
      align: "center",
      minWidth: 100,
      type: "number",
      valueFormatter: (
        value: GridCellParams<IInvoiceResponse, number | null>
      ) => {
        return value != null ? getCurrencyString(value) : "";
      },
    },
    {
      field: "amountDue",
      headerName: "Amt Due",
      flex: 1,
      headerAlign: "center",
      align: "center",
      minWidth: 100,
      type: "number",
      // Show amount due BEFORE discounts (saved discounts added back, plus what
      // this payment already applied in edit mode). Same source as the autofill.
      valueGetter: (_value, row) => getGrossAmountDue(row),
      valueFormatter: (
        value: GridCellParams<IInvoiceResponse, number | null>
      ) => {
        return value != null ? getCurrencyString(value) : "";
      },
    },
    {
      field: "payment",
      headerName: "Payment",
      flex: 1,
      minWidth: 100,
      headerAlign: "left",
      align: "center",
      renderCell: (params: GridRenderCellParams<IInvoiceResponse, string>) => {
        const rowIndex = invoiceIndexMap.get(params.id) ?? -1;
        const errorKey = `invoice-${params.id}`;
        const customError = paymentErrors[errorKey];
        const fieldError = errors?.invoicePayments?.[rowIndex]?.amount;

        return (
          <PaymentCell
            fieldIndex={rowIndex}
            control={control}
            rowId={params.id}
            canUpdatePayments={canUpdatePayments}
            onManualAmountChange={handleManualAmountChange}
            error={!!customError || !!fieldError}
          />
        );
      },
    },
    {
      field: "balance",
      headerName: "Balance",
      flex: 1,
      headerAlign: "center",
      minWidth: 100,
      align: "center",
      type: "number",
      valueGetter: (_value, row) => getInvoiceBalance(row),
      valueFormatter: (value: number | null) => {
        return value != null ? getCurrencyString(value) : "";
      },
      renderCell: (params: GridRenderCellParams<IInvoiceResponse, number>) => {
        const balance = params.value || 0;
        const isNegative = balance < -0.001;
        const isPartiallyPaid = balance > 0.001;

        const rowIndex = invoiceIndexMap.get(params.id) ?? -1;
        const hasPaymentValue =
          (invoicePaymentsWatch?.[rowIndex]?.amount || 0) > 0;

        const isRowSelected = selectionModel.includes(params.id);
        const shouldHighlight = isRowSelected || hasPaymentValue;

        let balanceColor = "text.primary";
        if (isNegative && shouldHighlight) {
          balanceColor = "error.main";
        } else if (isPartiallyPaid && shouldHighlight) {
          balanceColor = "warning.main";
        }

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <Typography
              variant="body2"
              color={balanceColor}
              fontWeight={isNegative && shouldHighlight ? "bold" : "normal"}
            >
              {getCurrencyString(balance)}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "adjustment",
      headerName: "",
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      align: "center",
      renderCell: (params: GridRenderCellParams<IInvoiceResponse, string>) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            height: "100%",
          }}
        >
          <MiMenu
            disabled={!canUpdatePayments || !amountReceived}
            optionalElement={
              <MiButton
                variant="outlined"
                size="small"
                title="Adjustment"
                disabled={!canUpdatePayments || !amountReceived}
                startIcon={{ path: mdiPlus }}
              />
            }
            data={[
              {
                label: "Discount",
                action: () => handleAdjustmentClick(params.id as number),
                disabled: (() => {
                  const invoiceIndex = invoiceIndexMap.get(params.id) ?? -1;
                  if (invoiceIndex === -1) return false;
                  const adjustments =
                    getValues(`invoicePayments.${invoiceIndex}.adjustments`) ||
                    [];

                  return (
                    adjustments.length > 0 ||
                    !accountDefaults.defaultDiscountPercentage
                  ); // Only allow one adjustment per invoice
                })(),
              },
              {
                label: "Retainage",
                disabled:
                  !!params.row.retainageParentId ||
                  !!params.row.retainageChildId ||
                  !accountDefaults.defaultRetainagePercentage,
                action: () => handleRetainageClick(params.row),
              },
            ]}
          />
        </Box>
      ),
    },
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      renderCell: (params) => (
        <CustomDetailPanelToggle id={params.id} value={params.value} />
      ),
    },
  ];

  const handleVoidCreditMemo = (id: string, modifiedOn: string) => {
    setCreditMemoToVoid({ id, modifiedOn });
  };

  const confirmVoidCreditMemo = async () => {
    if (creditMemoToVoid) {
      const response = await voidCreditMemo(
        creditMemoToVoid.id,
        creditMemoToVoid.modifiedOn
      );
      if (response?.status === HTTP_STATUSES.OK && customerAccountId) {
        getCreditMemos(customerAccountId);
      }
      setCreditMemoToVoid(null);
    }
  };

  const creditMemoColumns: GridColDef<ICreditMemoDetailsResponse>[] = [
    {
      field: "address",
      headerName: "Address",
      flex: 2,
      minWidth: 250,
      valueGetter: (value, row) => {
        return row?.home?.address?.addressLine1 || "";
      },
      renderCell: (
        params: GridRenderCellParams<ICreditMemoDetailsResponse, string>
      ) => {
        const address = params.row.home?.address?.addressLine1 || "";
        return (
          <Box display="flex" alignItems="center" height="100%">
            <Typography
              variant="body2"
              sx={{
                cursor: "pointer",
                color: "primary.main",
                textDecoration: "underline",
                "&:hover": {
                  color: "primary.dark",
                },
              }}
              onClick={() => handleDocumentClick(params.row)}
            >
              {address}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "date",
      headerName: "Date",
      flex: 1,
      minWidth: 110,
      valueFormatter: (value) => formatDate(value, "MM/DD/YYYY"),
    },
    {
      field: "openAmount",
      headerName: "Open Amount",
      flex: 1,
      headerAlign: "right",
      align: "right",
      minWidth: 100,
      type: "number",
      valueGetter: (_value, row) => getCreditMemoDisplayOpenAmount(row),
      renderCell: (params) => (
        <Box
          alignItems="center"
          display="flex"
          justifyContent="end"
          width="100%"
          height="100%"
        >
          <Typography color="error">
            ({getCurrencyString(Number(params.value) || 0)})
          </Typography>
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 50,
      sortable: false,
      renderCell: (params) => (
        <MiIconButton
          disabled={!canVoidCreditMemos}
          onClick={() =>
            handleVoidCreditMemo(params.row.id, params.row.modifiedOn)
          }
        >
          <MiIcon path={mdiCancel} color="grey" />
        </MiIconButton>
      ),
    },
  ];

  const paymentMethodOptions = [
    { label: "Cash", value: PaymentType.Cash },
    { label: "Check", value: PaymentType.Check },
    { label: "Credit Card", value: PaymentType.CreditCard },
    { label: "Other", value: PaymentType.Other },
  ];

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;
    setSearch(value);
  };

  const handleDocumentClick = (
    document: IInvoiceResponse | ICreditMemoDetailsResponse
  ) => {
    setSelectedDocument(document);
    setDrawerOpen(true);
  };

  const handleAddCreditMemo = () => {
    setCreditMemoModalOpen(true);
  };

  const handleCreditMemoSubmit = async (data: {
    homeId: string;
    poNumber: string;
    accountId: string;
    amount: number;
    notes: string;
  }) => {
    const response = await createCreditMemo(data);
    if (response.status === HTTP_STATUSES.OK && customerAccountId) {
      await getCreditMemos(customerAccountId);
      if (response.data?.id) {
        setCreditMemoSelectionModel((prev) => [...prev, response.data.id]);
      }
    }
  };

  const handleSelectionModelChange = (newSelection: GridRowSelectionModel) => {
    if (!amountReceived) {
      setError("amount", { type: "manual", message: "Enter a valid amount" });
      return;
    }
    const previousSelection = selectionModel;
    const added = newSelection.filter((id) => !previousSelection.includes(id));
    const removed = previousSelection.filter(
      (id) => !newSelection.includes(id)
    );

    // Zero out removed invoice allocations
    removed.forEach((id) => {
      const idx = invoiceIndexMap.get(id) ?? -1;
      if (idx >= 0) {
        setValue(`invoicePayments.${idx}.amount`, 0);
      }
    });

    // Only count allocated amounts from currently selected invoices (excluding those being removed)
    const remainingSelected = previousSelection.filter(
      (id) => !removed.includes(id)
    );
    const allocated = roundCurrency(
      remainingSelected.reduce((sum: number, id) => {
        const idx = invoiceIndexMap.get(id) ?? -1;
        if (idx >= 0) {
          const val = Number(getValues(`invoicePayments.${idx}.amount`)) || 0;
          return sum + val;
        }
        return sum;
      }, 0)
    );

    // Calculate remaining amount available for allocation
    let remaining = roundCurrency(
      amountReceived + totals.selectedCreditMemosTotal - allocated
    );
    if (remaining < 0) remaining = 0;

    const finalSelection = [
      ...previousSelection.filter((id) => !removed.includes(id)),
    ];

    // Allocate to newly added rows sequentially until money runs out
    let hasPartiallyPaid = false;
    added.forEach((id) => {
      if (remaining <= 0) return;

      const idx = invoiceIndexMap.get(id) ?? -1;
      if (idx >= 0) {
        const effectiveDue = getEffectiveAmountDue(idx);
        const toApply = roundCurrency(Math.min(effectiveDue, remaining));

        if (toApply > 0) {
          setValue(`invoicePayments.${idx}.amount`, toApply);
          remaining = roundCurrency(remaining - toApply);
          finalSelection.push(id);
          if (toApply < effectiveDue) {
            hasPartiallyPaid = true;
          }
        }
      }
    });

    if (hasPartiallyPaid) {
      toastService.warning({
        text: "Review the payment amount for each invoice, some may be partially applied",
      });
    }

    setSelectionModel(finalSelection);
  };

  const handleCreditMemoSelectionChange = (
    newSelection: GridRowSelectionModel
  ) => {
    // Require payment amount to be entered first (unless in edit mode with existing selections)
    if (
      !amountReceived &&
      newSelection.length > creditMemoSelectionModel.length
    ) {
      setError("amount", {
        type: "manual",
        message: "Enter amount received before selecting backcharges",
      });
      return;
    }

    // Validate selection: Allow memos with open balance OR already applied to this payment
    const validSelection = (newSelection as string[]).filter((id) => {
      const memo = creditMemos.find((m) => m.id === id);
      if (!memo) return false;

      // Allow if memo has available balance
      if (memo.openAmount > 0) return true;

      // In edit mode, allow if memo is already applied to THIS payment
      if (paymentId) {
        const wasAppliedToThisPayment = memo.creditMemoPayments?.some(
          (cp) => cp.paymentId === paymentId
        );
        if (wasAppliedToThisPayment) return true;
      }

      return false;
    });
    setCreditMemoSelectionModel(validSelection);
  };

  return (
    <>
      <MiDetailPageHeader
        title="Post Payments"
        loading={isLoading}
        customOnBack={() => navigate(`/accounting/payments`)}
      />
      <Box
        display="flex"
        flexDirection="row"
        flex={1}
        height="100%"
        overflow="hidden"
        m={2}
        gap={2}
      >
        <MiBox
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 0.3,
            minWidth: 250,
            height: "100%",
            overflow: "auto",
            padding: 0,
            margin: 0,
          }}
        >
          <Box>
            <Box p={3}>
              <Typography fontSize={14} fontWeight={600}>
                Payment Information
              </Typography>
            </Box>
            <Divider flexItem orientation="horizontal" />
            <Box p={3} gap={3} display="flex" flexDirection="column">
              <Typography fontSize={14} fontWeight={400} color="text.secondary">
                CUSTOMER
              </Typography>
              <Controller
                name="customerAccountId"
                control={control}
                render={({ field: { value, onChange } }) => {
                  const selectedOption =
                    customers.find((c) => c.id === value) || null;
                  return (
                    <MiAutocomplete
                      name="customerAccountId"
                      label="Customer"
                      disabled={!!paymentId}
                      options={customers}
                      value={selectedOption}
                      inputValue={customerInputValue}
                      getOptionLabel={(option: { id: string; label: string }) =>
                        option?.label ? option.label : ""
                      }
                      renderOption={(
                        props: React.HTMLAttributes<HTMLLIElement>,
                        option: { id: string; label: string }
                      ) => (
                        <li {...props} key={option.id}>
                          {option.label}
                        </li>
                      )}
                      required
                      onInputChange={(
                        event: React.ChangeEvent<unknown>,
                        text: string
                      ) => {
                        setCustomerInputValue(text);
                        if (text === "") {
                          onChange("");
                        }
                      }}
                      onChange={(
                        _: unknown,
                        opt: {
                          id: string;
                          label: string;
                          defaultRetainagePercentage: number;
                          defaultDiscountPercentage: number;
                        } | null
                      ) => {
                        const id = opt?.id || "";
                        onChange(id);
                        setCustomerInputValue(opt?.label || "");
                      }}
                    />
                  );
                }}
              />
            </Box>
            <Box p={3} mb={4} gap={3} display="flex" flexDirection="column">
              <Typography fontSize={14} fontWeight={400} color="text.secondary">
                PAYMENT
              </Typography>
              <MiControlledSelectBox
                name="paymentType"
                formControl={control}
                label="Payment Method"
                options={paymentMethodOptions}
                required
                disableNone
              />
              <Controller
                name="amount"
                control={control}
                render={({ field: { ref, onChange, ...field } }) => (
                  <NumericFormat
                    {...field}
                    value={field.value || ""}
                    customInput={MiInputTextbox}
                    thousandSeparator
                    required
                    label="Amount Received"
                    onValueChange={(values) => {
                      onChange(values.floatValue ?? 0);
                    }}
                    onBlur={(e) => {
                      field.onBlur();
                      if (paymentData && field.value !== paymentData.amount) {
                        toastService.warning({
                          text: "Review the payment amount for each invoice, some may be partially applied",
                        });
                      }
                    }}
                    decimalScale={2}
                    fixedDecimalScale
                    allowNegative
                    allowLeadingZeros={false}
                    prefix="$"
                    name={field.name}
                    textfieldProps={{
                      sx: {
                        size: "small",
                        fontSize: "0.875rem",
                      },
                    }}
                    error={errors?.amount}
                    helperText={errors?.amount?.message}
                  />
                )}
              />
              {paymentType === PaymentType.Check && (
                <MiControlledTextField
                  name="checkNumber"
                  formControl={control}
                  label="Check #"
                  required
                />
              )}
              {paymentType === PaymentType.CreditCard && (
                <MiControlledTextField
                  name="creditCardReferenceNumber"
                  formControl={control}
                  label="Reference"
                  required
                />
              )}
              <MiControlledDatePicker
                name="depositDate"
                control={control}
                label="Deposit Date"
                required
                sx={{ width: FIELD_WIDTH }}
              />
              <MiControlledSelectBox
                name="bankAccountQuickBooksId"
                formControl={control}
                disableNone
                label="Bank Account"
                options={bankAccountsOptions}
                required
              />
            </Box>
            {/* // Payment summary */}
            <Box
              px={4}
              mb={4}
              gap={2}
              display="flex"
              flexDirection="column"
              data-testid="post-payment-payment-summary"
            >
              <Typography fontSize={14} fontWeight={400} color="text.secondary">
                Payment Summary
              </Typography>
              <Box
                display="flex"
                flexDirection="column"
                gap={1}
                p={2}
                borderRadius={1}
                border="1px solid #eee"
                bgcolor={theme.palette.grey[50]}
              >
                <Box display="flex" justifyContent="space-between">
                  <Typography fontSize={14}>Amount Received:</Typography>
                  <Typography fontSize={14}>
                    {getCurrencyString(amountReceived)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography fontSize={14}>Allocated:</Typography>
                  <Typography fontSize={14} color="error.main">
                    ({getCurrencyString(totals.totalPayments)})
                  </Typography>
                </Box>

                <Divider flexItem orientation="horizontal" />
                <Box display="flex" justifyContent="space-between">
                  <Typography fontSize={14}>Unapplied Amount:</Typography>
                  <Typography
                    fontSize={14}
                    color={
                      roundCurrency(
                        amountReceived +
                          totals.selectedCreditMemosTotal -
                          totals.totalPayments
                      ) !== 0
                        ? "orange"
                        : "inherit"
                    }
                  >
                    {getCurrencyString(
                      roundCurrency(
                        amountReceived +
                          totals.selectedCreditMemosTotal -
                          totals.totalPayments
                      )
                    )}
                  </Typography>
                </Box>
              </Box>
            </Box>
            {/* // Selected Invoices summary */}
            <Box
              px={4}
              gap={2}
              display="flex"
              flexDirection="column"
              data-testid="post-payment-selected-invoices-summary"
            >
              <Typography fontSize={14} fontWeight={400} color="text.secondary">
                Selected Invoices Summary
              </Typography>
              <Box
                display="flex"
                flexDirection="column"
                gap={1}
                p={2}
                borderRadius={1}
                border="1px solid #eee"
                bgcolor={theme.palette.grey[50]}
              >
                <Box display="flex" justifyContent="space-between">
                  <Typography fontSize={14}>Selected Invoices:</Typography>
                  <Typography fontSize={14}>
                    {totals.selectedInvoices.toString()}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography fontSize={14}>Amount Due:</Typography>
                  <Typography fontSize={14}>
                    {getCurrencyString(totals.totalAmountDue)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography fontSize={14}>Total Payments:</Typography>
                  <Typography fontSize={14}>
                    {getCurrencyString(totals.totalPayments)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography fontSize={14}>Discounts:</Typography>
                  <Typography fontSize={14}>
                    {getCurrencyString(totals.discountTotal)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography fontSize={14}>Backcharges:</Typography>
                  <Typography fontSize={14} color="error.main">
                    ({getCurrencyString(totals.backcharges)})
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography fontSize={14}>Retainage:</Typography>
                  <Typography fontSize={14}>
                    {getCurrencyString(totals.retainageTotal)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </MiBox>
        <MiBox
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            height: "100%",
            overflow: "hidden",
            padding: 0,
            margin: 0,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            mb={2}
            p={2}
            justifyContent="space-between"
          >
            <Typography
              fontSize={14}
              fontWeight={600}
              className={classes.title}
            >
              Invoices
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <MiInputTextbox
                value={search}
                style={{
                  maxWidth: 220,
                  marginLeft: theme.spacing(2),
                  backgroundColor: theme.palette.common.white,
                }}
                handleChange={handleSearch}
                placeholder="Search Invoices"
                textfieldProps={{
                  style: {
                    fontSize:
                      (theme as unknown as { fontSize: { medium: number } })
                        .fontSize?.medium || 14,
                    height: 34,
                  },
                  startAdornment: (
                    <InputAdornment position="start">
                      <MiIcon path={mdiMagnify} size={0.8} color="grey" />
                    </InputAdornment>
                  ),
                }}
              />
              <MiButton
                variant="outlined"
                title="Add Backcharge"
                startIcon={{ path: mdiPlus }}
                onClick={handleAddCreditMemo}
                disabled={!customerAccountId || !canCreateCreditMemos}
              />
            </Box>
          </Box>
          <Box
            sx={{
              flex: 1,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ flex: 1, overflow: "hidden" }}>
              <DataGridPremium
                data-testid="post-payment-data-grid"
                apiRef={apiRef}
                rows={invoices}
                columns={columns}
                hideFooter={false}
                pagination={false}
                checkboxSelection
                disableRowSelectionOnClick
                disableColumnMenu
                rowSelectionModel={selectionModel}
                onRowSelectionModelChange={handleSelectionModelChange}
                hideFooterRowCount
                getRowClassName={(params) => {
                  const idx = invoiceIndexMap.get(params.id) ?? -1;
                  const paymentAmount =
                    invoicePaymentsWatch?.[idx]?.amount || 0;
                  const isSelected = selectionModel.includes(params.id);
                  const hasPayment = paymentAmount > 0;
                  if (isSelected || hasPayment) {
                    const balance =
                      apiRef.current.getCellValue(params.id, "balance") || 0;
                    if (balance > 0.001) {
                      return "row--partially-paid";
                    }
                  }
                  return "";
                }}
                slots={{
                  noRowsOverlay: CustomNoRowsOverlay,
                  footer: CustomFooter,
                }}
                getDetailPanelContent={getDetailPanelContent}
                getDetailPanelHeight={() => "auto"}
                slotProps={{
                  toolbar: {
                    showQuickFilter: true,
                  },
                  loadingOverlay: {
                    variant: "linear-progress",
                    noRowsVariant: "skeleton",
                  },
                  footer: {
                    count: invoices.length,
                    selectedCount: selectionModel.length,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  } as any,
                  row: {
                    "data-testid": "post-payment-invoice",
                  },
                }}
                sx={{
                  "--unstable_DataGrid-radius": "0px",
                  [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]:
                    {
                      outline: "none",
                    },
                  [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
                    {
                      outline: "none",
                    },
                  "& .MuiDataGrid-root": {
                    border: "none",
                  },
                  "& .MuiDataGrid-cell": {
                    borderBottom: "1px solid #f0f0f0",
                    "&:focus": {
                      outline: "none !important",
                    },
                    "&:focus-within": {
                      outline: "none !important",
                    },
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#f5f5f5",
                    borderBottom: "1px solid #e0e0e0",
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontWeight: 600,
                  },
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: "#fafafa",
                  },
                  "& .MuiDataGrid-row.row--partially-paid": {
                    backgroundColor: theme.palette.warning.light,
                    "&:hover": {
                      backgroundColor: theme.palette.warning.light,
                    },
                  },
                }}
                detailPanelExpandedRowIds={[...expandedRows]}
                onDetailPanelExpandedRowIdsChange={(ids) =>
                  setExpandedRows(new Set(ids as (string | number)[]))
                }
              />
            </Box>
            <Box
              sx={{
                height: "35%",
                display: "flex",
                overflow: "visible",
                flexDirection: "column",
                borderTop: "1px solid #e0e0e0",
              }}
              data-testid="post-payment-credit-memo-area"
            >
              <Box p={1} bgcolor="#FFF8E1">
                <Typography fontWeight={600} py={3} px={1} fontSize={14}>
                  Credit Memos/Backcharges
                </Typography>
              </Box>
              <Box sx={{ flex: 1, overflow: "hidden" }}>
                <DataGridPremium
                  rows={creditMemos}
                  columns={creditMemoColumns}
                  checkboxSelection
                  disableRowSelectionOnClick
                  rowSelectionModel={creditMemoSelectionModel}
                  onRowSelectionModelChange={handleCreditMemoSelectionChange}
                  isRowSelectable={(params) => {
                    const memo = params.row as ICreditMemoDetailsResponse;
                    // Allow selection if memo has available balance
                    if (memo.openAmount > 0) return true;
                    // In edit mode, allow if already applied to THIS payment
                    if (paymentId) {
                      const wasAppliedToThisPayment =
                        memo.creditMemoPayments?.some(
                          (cp) => cp.paymentId === paymentId
                        );
                      if (wasAppliedToThisPayment) return true;
                    }
                    return false;
                  }}
                  slots={{
                    footer: CustomFooter,
                  }}
                  slotProps={{
                    footer: {
                      count: creditMemos.length,
                      selectedCount: creditMemoSelectionModel.length,
                      label: "Credit Memos/Backcharges",
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    } as any,
                    row: {
                      "data-testid": "post-payment-credit-memo",
                    },
                  }}
                  sx={{
                    "--unstable_DataGrid-radius": "0px",
                    [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]:
                      {
                        outline: "none",
                      },
                    [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
                      {
                        outline: "none",
                      },
                    "& .MuiDataGrid-root": {
                      border: "none",
                    },
                    "& .MuiDataGrid-cell": {
                      borderBottom: "1px solid #f0f0f0",
                      "&:focus": {
                        outline: "none !important",
                      },
                      "&:focus-within": {
                        outline: "none !important",
                      },
                    },
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: "#f5f5f5",
                      borderBottom: "1px solid #e0e0e0",
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                      fontWeight: 600,
                    },
                    "& .MuiDataGrid-row:hover": {
                      backgroundColor: "#fafafa",
                    },
                    "& .MuiDataGrid-row.Mui-disabled": {
                      opacity: 0.5,
                      cursor: "not-allowed",
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>
        </MiBox>
        <InvoiceViewerDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          selectedDocument={selectedDocument}
        />

        <CreateCreditMemoModal
          open={creditMemoModalOpen}
          accountId={customerAccountId}
          onClose={() => setCreditMemoModalOpen(false)}
          onSubmit={handleCreditMemoSubmit}
        />

        <CreateRetainageModal
          open={retainageModalOpen}
          invoice={retainageInvoice}
          onClose={() => setRetainageModalOpen(false)}
          onSubmit={handleRetainageSubmit}
          defaults={{
            retainagePercent: accountDefaults.defaultRetainagePercentage,
          }}
        />

        <MiModalConfirm
          title="Void Credit Memo"
          open={!!creditMemoToVoid}
          handleNegative={() => setCreditMemoToVoid(null)}
          buttonPositiveColor={BUTTON_COLORS.ERROR}
          handlePositive={confirmVoidCreditMemo}
          titleIcon={
            {
              path: mdiCancel,
              color: theme.palette.error.main,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any
          }
          description="Are you sure you want to delete this credit memo?"
        />
      </Box>

      <Footer
        show={isDirty || hasCreditMemoChanges}
        actions={footerActions}
        disableSave={!isValid || isLoading || hasPaymentErrors}
      />
    </>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  actionContainer: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
  },
  title: {
    color: theme.palette.text.primary,
    paddingLeft: theme.spacing(1),
  },
}));
